import { ref } from 'vue'
import {
  showSupportTicket,
  createSupportTicket,
  replySupportTicket,
  closeSupportTicket,
  reopenSupportTicket,
} from '@/api/support'
import type { SupportTicket, StoreSupportTicketPayload, StoreSupportMessagePayload } from '@/types/support'

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { status: unknown } }).response.status !== undefined
  )
}

export function useSupportTicket() {
  const ticket = ref<SupportTicket | null>(null)
  const loading = ref(false)
  const submitting = ref(false)
  const error = ref<string | null>(null)
  const formErrors = ref<Record<string, string[]>>({})
  const closedError = ref<string | null>(null)

  async function load(organisationId: number, ticketId: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      ticket.value = await showSupportTicket(organisationId, ticketId)
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response.status === 403) {
        error.value = 'support.thread.error'
      } else if (isAxiosError(e) && e.response.status === 404) {
        error.value = 'support.thread.error'
      } else {
        error.value = 'support.thread.error'
      }
    } finally {
      loading.value = false
    }
  }

  async function create(organisationId: number, payload: StoreSupportTicketPayload): Promise<SupportTicket | null> {
    submitting.value = true
    formErrors.value = {}
    try {
      const created = await createSupportTicket(organisationId, payload)
      ticket.value = created
      return created
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response.status === 422 && e.response.data.errors) {
        formErrors.value = e.response.data.errors
      } else {
        error.value = 'common.error_generic'
      }
      return null
    } finally {
      submitting.value = false
    }
  }

  async function reply(organisationId: number, ticketId: number, payload: StoreSupportMessagePayload): Promise<boolean> {
    submitting.value = true
    formErrors.value = {}
    closedError.value = null
    try {
      const message = await replySupportTicket(organisationId, ticketId, payload)
      if (ticket.value) {
        ticket.value = {
          ...ticket.value,
          messages: [...(ticket.value.messages ?? []), message],
          last_activity_at: message.created_at,
        }
      }
      return true
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response.status === 409) {
        closedError.value = 'support.thread.reply_closed_error'
      } else if (isAxiosError(e) && e.response.status === 422 && e.response.data.errors) {
        formErrors.value = e.response.data.errors
      } else {
        error.value = 'common.error_generic'
      }
      return false
    } finally {
      submitting.value = false
    }
  }

  async function close(organisationId: number, ticketId: number): Promise<boolean> {
    submitting.value = true
    try {
      const updated = await closeSupportTicket(organisationId, ticketId)
      ticket.value = updated.messages !== undefined
        ? { ...updated, messages: ticket.value?.messages ?? [] }
        : { ...updated, messages: ticket.value?.messages }
      return true
    } catch {
      return false
    } finally {
      submitting.value = false
    }
  }

  async function reopen(organisationId: number, ticketId: number): Promise<boolean> {
    submitting.value = true
    try {
      const updated = await reopenSupportTicket(organisationId, ticketId)
      ticket.value = updated.messages !== undefined
        ? { ...updated, messages: ticket.value?.messages ?? [] }
        : { ...updated, messages: ticket.value?.messages }
      return true
    } catch {
      return false
    } finally {
      submitting.value = false
    }
  }

  return { ticket, loading, submitting, error, formErrors, closedError, load, create, reply, close, reopen }
}
